import React, {
  useMemo, useState, useRef, useCallback, useEffect, Fragment, RefObject, ReactElement,
} from 'react';
import FieldText from '../field-text/field-text.component';
import FieldSelect from '../field-select/field-select.component';
import MenuItemAddNewItem from '../menu-item-add-new-item/menu-item-add-new-item.component';
import MenuItemList from '../menu-item-list/menu-item-list.component';
import useStylesFieldTextNewItem from './field-text-new-item.styles';
import { KEYBOARD_KEY_ENTER } from '../../constants/system/keyboard-keys';

const FieldTextNewItem: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const INPUT_BLANK_VALUE: string = '';
  const ITEM_LABEL_DEFAULT: string = 'Item';

  const {
    itemList,
    itemLabel,
    itemLabelKey,
    onItemSearch,
    onItemSearchClose,
    onAddItem,
  } = props;

  const { classes } = useStylesFieldTextNewItem();
  const textFieldRef: RefObject<HTMLInputElement> | null = useRef(null);

  const [isNewItemFieldVisible, setIsNewItemFieldVisible]: [boolean, Function] = useState(false);

  const menuItemItemList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: itemLabelKey,
    optionList: itemList,
  }), [itemList]);

  const addItem: Function = (event: React.SyntheticEvent, newItemValue: string): void => {
    if (onAddItem && newItemValue && newItemValue.trim()) {
      onAddItem(event, newItemValue);
    }

    setIsNewItemFieldVisible(false);
  };

  const onKeyDown: Function = (event: React.KeyboardEvent): void => {
    if (event.key === KEYBOARD_KEY_ENTER) {
      const inputField: HTMLInputElement = event.target as HTMLInputElement;

      if (inputField.value !== INPUT_BLANK_VALUE) {
        addItem(event, inputField.value);

        inputField.value = INPUT_BLANK_VALUE;
      }

      event.preventDefault();
    }
  };

  const onItemChange: Function = (event: React.ChangeEvent, value: string): void => {
    if (value && value !== INPUT_BLANK_VALUE) {
      addItem(event, value);
    }
  };

  const showNewItemField: Function = useCallback((): void => {
    setIsNewItemFieldVisible(true);
  }, []);

  useEffect((): void => {
    if (isNewItemFieldVisible && textFieldRef && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isNewItemFieldVisible]);

  return (
    <Fragment>
      {
        isNewItemFieldVisible ? (
          <FieldText
            isPlain
            isBordered
            inputRef={textFieldRef}
            placeholder={`New ${itemLabel || ITEM_LABEL_DEFAULT} Name`}
            className={classes.fieldTextNewItem}
            onKeyDown={onKeyDown}
            onBlur={(event: React.ChangeEvent): void => {
              const inputField: HTMLInputElement = event.target as HTMLInputElement;

              if (inputField.value !== '') {
                addItem(event, inputField.value);

                inputField.value = '';
              } else {
                setIsNewItemFieldVisible(false);
              }
            }}
          />
        ) : (
          <FieldSelect
            isPlain
            isBordered
            withSearch
            withBlankElement
            withBlankItemDefault
            className={classes.fieldTextNewItem}
            blankElementLabel={`Add New ${itemLabel || ITEM_LABEL_DEFAULT}`}
            onChange={(event: EventSelectChange, value: string): void => {
              onItemChange(event, value);
            }}
            onSearch={onItemSearch}
            onClose={onItemSearchClose}
            newItemComponent={(
              <MenuItemAddNewItem
                label={`New ${itemLabel || ITEM_LABEL_DEFAULT}`}
                onClick={showNewItemField}
              />
            )}
          >
            {menuItemItemList}
          </FieldSelect>
        )
      }
    </Fragment>
  );
});

export default FieldTextNewItem;
